import { Link, Navigate, useParams } from 'react-router-dom'

import { Box, Text } from '@factorialco/deprecated-design-system'
import {
  PerformanceGenerateQuestionnaireUsingAiDocument,
  PerformanceProcessOverviewDraftSectionableDocument,
  PerformanceReviewerStrategiesStrategyEnum,
  UpdateDefaultRatingScaleReviewTypeQuestionnaireDocument,
  UpdateReviewTypeQuestionnaireDocument,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { MissingInformation } from 'modules/performance_process/components/review/show/MissingInformation'
import {
  type HandleUpdateQuestionnaire,
  ReviewTypeQuestions,
} from 'modules/performance_process/components/review/show/ReviewTypeQuestions'
import { settingsUrl } from 'modules/performance_process/components/review/show/helpers/routingHelper'
import { validationChecker } from 'modules/performance_process/components/review/show/helpers/validationChecker'
import { useNewPerformanceModuleContext } from 'modules/performance_process/pages/ReviewsRootPage'
import type { Scale } from 'modules/questionnaires/lib/types'

const ReviewOverviewDraftPage = () => {
  const params = useParams()
  const { navigation } = useNavigation()
  const { hasCustomScoreConfigurationEnabled } = useNewPerformanceModuleContext()

  const reviewId = String(params.reviewId)

  const { data, loading, error } = useQuery(PerformanceProcessOverviewDraftSectionableDocument, {
    variables: {
      id: reviewId,
    },
  })

  const draftReview = data?.performance?.reviewProcess

  const [updateQuestionnaire, { loading: updatingQuestionnaire }] = useMutation(
    UpdateReviewTypeQuestionnaireDocument,
    {
      extractorFn: (mutationData) =>
        mutationData.performanceMutations
          .updateQuestionnaireForStrategyPerformanceReviewQuestionnaireByStrategy,
    }
  )

  const [generateQuestionnaireUsingAI] = useMutation(
    PerformanceGenerateQuestionnaireUsingAiDocument,
    {
      extractorFn: (mutationData) =>
        mutationData.performanceMutations
          .parseAndUpdateQuestionnaireForStrategyPerformanceReviewQuestionnaireByStrategy,
    }
  )

  const handleQuestionnaireUpdate: HandleUpdateQuestionnaire = async (
    questionnaireContent,
    strategy
  ) => {
    const response = await updateQuestionnaire({
      variables: {
        performanceReviewProcessId: String(reviewId),
        questionnaireContent,
        strategy,
      },
    })
    const result = handleResult(response, () => {})
    return { success: result.success }
  }

  const [updateDefaultRatingScale, { loading: updatingDefaultRatingScale }] = useMutation(
    UpdateDefaultRatingScaleReviewTypeQuestionnaireDocument,
    {
      extractorFn: (mutationData) =>
        mutationData.performanceMutations
          .updateDefaultRatingScalePerformanceReviewQuestionnaireByStrategy,
    }
  )
  const handleDefaultRatingChange = async (scales: Scale[]) => {
    const response = await updateDefaultRatingScale({
      variables: {
        defaultRatingScale: scales,
        performanceReviewProcessId: String(reviewId),
      },
    })

    const result = handleResult(response, () => {})
    return { success: result.success }
  }

  const handleSubmitGenerateUsingAI = async (
    content: string,
    strategy: PerformanceReviewerStrategiesStrategyEnum
  ) => {
    return generateQuestionnaireUsingAI({
      variables: {
        performanceReviewProcessId: String(reviewId),
        strategy,
        content,
      },
    })
  }

  if (draftReview && draftReview.status !== 'draft') {
    return (
      <Navigate
        replace
        to={{
          pathname: navigation.performanceProcess.reviews.review.fullPath({
            reviewId: String(reviewId),
          }),
          search: location.search,
        }}
      />
    )
  }

  return (
    <ResponsivePage.NestedBody loading={loading} error={error}>
      {draftReview?.reviewerStrategies && draftReview.questionnaires && (
        <Box gap="s16">
          <Text.Header2 color="grey1000">
            {i18n.t('performance_process.reviews.questionnaire_configuration')}
          </Text.Header2>

          {validationChecker(draftReview.startValidationErrors, 'missing_reviewer_strategy') ? (
            <Link to={settingsUrl(reviewId, navigation, 'review_types')}>
              <MissingInformation
                description={i18n.t('performance_process.reviews.missing_questions_general')}
              />
            </Link>
          ) : (
            <ReviewTypeQuestions
              questionnaireableId={reviewId}
              questionnaireableType="review_process"
              reviewType={draftReview.reviewerStrategies}
              questionnaires={draftReview.questionnaires}
              defaultScoringRange={draftReview.questionnaires.defaultRatingScale}
              generateQuestionsUsingAiEnabled={
                draftReview._policies?.generateReviewQuestionsUsingAi.can || false
              }
              onUpdateQuestionnaire={handleQuestionnaireUpdate}
              updatingQuestionnaire={updatingQuestionnaire}
              handleSubmitRating={{
                submitting: updatingDefaultRatingScale,
                onSubmit: handleDefaultRatingChange,
              }}
              handleSubmitGenerateUsingAI={handleSubmitGenerateUsingAI}
              scoreConfiguration={draftReview.scoreConfiguration}
              hasCustomScoreConfigurationEnabled={hasCustomScoreConfigurationEnabled}
            />
          )}
        </Box>
      )}
    </ResponsivePage.NestedBody>
  )
}

export default ReviewOverviewDraftPage
